import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { GraduationCap, ArrowRight, School, User, Lock, Mail, Phone, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiErrorMessage } from '../lib/apiError';
import { MIN_PASSWORD_LENGTH } from '../lib/passwordPolicy';

const SchoolRegister = ({ onBack, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [step, setStep] = useState(1); // 1=school info, 2=admin info, 3=success
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    school_name: '',
    phone: '',
    admin_full_name: '',
    admin_username: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!form.school_name.trim()) return toast.error(t('sr_school_name_required'));
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.admin_password !== form.admin_password_confirm) {
      return toast.error(t('sr_pwd_mismatch'));
    }
    if (form.admin_password.length < MIN_PASSWORD_LENGTH) {
      return toast.error(t('sr_pwd_too_short'));
    }
    setLoading(true);
    try {
      await axios.post('/schools/register', {
        school_name: form.school_name,
        phone: form.phone,
        admin_full_name: form.admin_full_name,
        admin_username: form.admin_username,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
      });
      setStep(3);
    } catch (error) {
      toast.error(apiErrorMessage(error, t('sr_generic_error')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-teal-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-700 to-teal-600 rounded-2xl flex items-center justify-center shadow-xl">
              <GraduationCap className="w-9 h-9 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-black bg-gradient-to-r from-teal-700 to-teal-600 bg-clip-text text-transparent">EduGate</h1>
          <p className="text-gray-500 mt-1">{t('sr_tagline')}</p>
        </div>

        {/* Progress */}
        {step < 3 && (
          <div className="flex items-center gap-3 mb-8">
            {[1, 2].map(s => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 flex-1 ${step >= s ? 'text-teal-600' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                    step > s ? 'bg-teal-700 border-teal-600 text-white' :
                    step === s ? 'border-teal-600 text-teal-600' :
                    'border-gray-300 text-gray-300'
                  }`}>
                    {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                  </div>
                  <span className="text-sm font-medium hidden sm:block">
                    {s === 1 ? t('sr_step_school') : t('sr_step_admin')}
                  </span>
                </div>
                {s < 2 && <div className={`h-0.5 flex-1 rounded ${step > 1 ? 'bg-teal-600' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step 1 — School Info */}
        {step === 1 && (
          <form onSubmit={handleStep1} className="bg-white rounded-3xl shadow-xl p-8 space-y-5">
            <div className="space-y-2">
              <Label className="font-semibold text-gray-800">{t('sr_school_name')}</Label>
              <div className="relative">
                <School className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  value={form.school_name}
                  onChange={e => set('school_name', e.target.value)}
                  placeholder={t('sr_school_name_placeholder')}
                  className="pr-10 h-11"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-gray-800">{t('sr_phone_optional')}</Label>
              <div className="relative">
                <Phone className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                <Input
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="+223 XX XX XX XX"
                  className="pr-10 h-11"
                  type="tel"
                />
              </div>
            </div>

            <div className="bg-teal-50 rounded-2xl p-4 text-sm text-teal-700 font-medium">
              {t('sr_trial_banner')}
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-teal-800 to-teal-700 text-white font-bold text-base rounded-xl flex items-center justify-center gap-2"
            >
              {t('sr_next')}
              <ArrowRight className="w-5 h-5" />
            </Button>

            <button type="button" onClick={onBack} className="w-full text-center text-gray-500 text-sm hover:text-gray-700">
              {t('sr_back_landing')}
            </button>
          </form>
        )}

        {/* Step 2 — Admin Info */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl p-8 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label className="font-semibold text-gray-800">{t('sr_admin_full_name')}</Label>
                <div className="relative">
                  <User className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                  <Input value={form.admin_full_name} onChange={e => set('admin_full_name', e.target.value)} placeholder={t('sr_admin_full_name_placeholder')} className="pr-10 h-11" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-gray-800">{t('sr_admin_username')}</Label>
                <Input value={form.admin_username} onChange={e => set('admin_username', e.target.value)} placeholder={t('sr_username_placeholder')} className="h-11" required />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-gray-800">{t('sr_admin_email')}</Label>
                <div className="relative">
                  <Mail className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                  <Input value={form.admin_email} onChange={e => set('admin_email', e.target.value)} placeholder="mail@school.com" type="email" className="pr-10 h-11" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-gray-800">{t('sr_admin_password')}</Label>
                <div className="relative">
                  <Lock className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    value={form.admin_password}
                    onChange={e => set('admin_password', e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('sr_admin_password_placeholder')}
                    className="pr-10 pl-10 h-11"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-3 top-3">
                    {showPassword ? <EyeOff className="w-5 h-5 text-muted-foreground" /> : <Eye className="w-5 h-5 text-muted-foreground" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-gray-800">{t('sr_admin_password_confirm')}</Label>
                <div className="relative">
                  <Lock className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    value={form.admin_password_confirm}
                    onChange={e => set('admin_password_confirm', e.target.value)}
                    type="password"
                    placeholder={t('sr_admin_password_confirm_placeholder')}
                    className="pr-10 h-11"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 h-11">
                {t('sr_previous')}
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 bg-gradient-to-r from-teal-800 to-teal-700 text-white font-bold rounded-xl"
                disabled={loading}
              >
                {loading ? t('sr_creating') : t('sr_create_account')}
              </Button>
            </div>
          </form>
        )}

        {/* Step 3 — Success */}
        {step === 3 && (
          <div className="bg-white rounded-3xl shadow-xl p-10 text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-black text-gray-900">{t('sr_success_title')}</h2>
            <p className="text-gray-600 leading-relaxed">
              <Trans i18nKey="sr_success_welcome" components={{ strong: <strong /> }} /><br />
              <Trans i18nKey="sr_success_trial" components={{ strong: <strong /> }} />
            </p>
            <div className={`bg-teal-50 rounded-2xl p-4 ${isRTL ? 'text-right' : 'text-left'} space-y-2`}>
              <p className="text-sm font-semibold text-teal-800">{t('sr_login_info')}</p>
              <p className="text-sm text-teal-600">
                <Trans i18nKey="sr_login_username" values={{ username: form.admin_username }} components={{ strong: <strong /> }} />
              </p>
              <p className="text-sm text-teal-600">
                <Trans i18nKey="sr_login_role" components={{ strong: <strong /> }} />
              </p>
            </div>
            <Button
              onClick={onSuccess}
              className="w-full h-12 bg-gradient-to-r from-teal-800 to-teal-700 text-white font-bold text-base rounded-xl"
            >
              {t('sr_goto_login')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolRegister;
